import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CircleStackIcon , ShieldCheckIcon, EyeIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const CookiesPage = () => {
  const cookieTypes = [
    {
      name: 'Cookies essentiels',
      description: 'Nécessaires au fonctionnement de la plateforme',
      examples: 'Authentification, session, sécurité',
      icon: ShieldCheckIcon
    },
    {
      name: 'Cookies de performance',
      description: 'Nous aident à améliorer notre service',
      examples: 'Analytics, performance du site',
      icon: EyeIcon
    },
    {
      name: 'Cookies fonctionnels',
      description: 'Personnalisent votre expérience',
      examples: 'Préférences de langue, sauvegarde des recherches',
      icon: Cog6ToothIcon
    },
    {
      name: 'Cookies publicitaires',
      description: 'Adaptent les publicités à vos centres d\'intérêt',
      examples: 'Recommandations, campagnes marketing',
      icon: CircleStackIcon 
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12">
      <div className="container-custom max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111111] rounded-2xl border border-[#252525] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#F97316] to-[#e8630a] px-8 py-10 text-white">
            <h1 className="text-3xl lg:text-4xl font-display font-bold mb-2">
              Politique des cookies
            </h1>
            <p className="text-white/80 text-lg">
              Comment nous utilisons les cookies sur VADOM
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-white/70">
              <CircleStackIcon  className="h-5 w-5" />
              <span>Dernière mise à jour : 1er mai 2024</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8">
            {/* Introduction */}
            <div className="mb-6 pb-4 border-b border-[#252525]">
              <p className="text-[#AAAAAA] leading-relaxed">
                Cette politique explique comment VADOM utilise les cookies et technologies similaires 
                pour reconnaître les visiteurs de notre plateforme, améliorer leur expérience, 
                et analyser notre trafic. En continuant à utiliser notre site, vous acceptez 
                l'utilisation de cookies conformément à cette politique.
              </p>
            </div>

            {/* Qu'est-ce qu'un cookie */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Qu'est-ce qu'un cookie ?</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Un cookie est un petit fichier texte stocké sur votre ordinateur ou appareil mobile 
                lorsque vous visitez un site web. Il permet au site de se souvenir de vos actions 
                et préférences pendant une période donnée, vous évitant ainsi de ressaisir certaines 
                informations à chaque visite.
              </p>
            </section>

            {/* Types de cookies */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Types de cookies utilisés</h2>
              <div className="grid grid-cols-1 gap-4">
                {cookieTypes.map((cookie, idx) => (
                  <div key={idx} className="bg-[#0a0a0a] rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <cookie.icon className="h-5 w-5 text-[#F97316]" />
                      <h3 className="font-semibold text-white">{cookie.name}</h3>
                    </div>
                    <p className="text-[#AAAAAA] text-sm mb-2">{cookie.description}</p>
                    <p className="text-[#666666] text-xs">Exemples : {cookie.examples}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Cookies tiers */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Cookies tiers</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Notre plateforme peut contenir des cookies de nos partenaires (réseaux sociaux, 
                services d'analyse, prestataires de paiement). Ces cookies sont gérés par des tiers 
                et soumis à leurs propres politiques de confidentialité.
              </p>
            </section>

            {/* Durée de conservation */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Durée de conservation</h2>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li><span className="text-white">Cookies de session :</span> expirés à la fermeture du navigateur</li>
                <li><span className="text-white">Cookies persistants :</span> conservés jusqu'à 13 mois maximum</li>
              </ul>
            </section>

            {/* Gestion des cookies */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Gérer vos préférences</h2>
              <p className="text-[#AAAAAA] leading-relaxed mb-3">
                Vous pouvez à tout moment modifier vos préférences en matière de cookies :
              </p>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li>Via notre <button className="text-[#F97316] hover:underline">gestionnaire de cookies</button> disponible sur le site</li>
                <li>En configurant votre navigateur (paramètres de confidentialité)</li>
                <li>En utilisant des extensions comme "Privacy Badger" ou "Ghostery"</li>
              </ul>
              <div className="mt-4 p-4 bg-[#0a0a0a] rounded-lg">
                <p className="text-sm text-[#666666]">
                  <span className="text-white">Pour info :</span> La désactivation des cookies peut affecter 
                  certaines fonctionnalités de la plateforme.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Contact</h2>
              <div className="bg-[#0a0a0a] rounded-xl p-5">
                <p className="text-[#AAAAAA] mb-2">Pour toute question sur l'utilisation des cookies :</p>
                <p className="text-white">📧 <a href="mailto:vadomxpress@gmail.com" className="hover:text-[#F97316] transition">vadomxpress@gmail.com</a></p>
                <p className="text-white">🌐 <Link to="/contact" className="hover:text-[#F97316] transition">Formulaire de contact</Link></p>
              </div>
            </section>

            {/* CTA */}
            <div className="mt-6 pt-4 border-t border-[#252525] text-center">
              <p className="text-[#AAAAAA] mb-3">Pour plus d'informations sur la gestion de vos données, consultez notre</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/legal/privacy" className="text-[#F97316] hover:underline">Politique de confidentialité</Link>
                <span className="text-[#666666]">•</span>
                <Link to="/legal/terms" className="text-[#F97316] hover:underline">Conditions d'utilisation</Link>
                <span className="text-[#666666]">•</span>
                <Link to="/legal/legal-notice" className="text-[#F97316] hover:underline">Mentions légales</Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiesPage;